import React from 'react';
import Header from "../components/Header";
import Footer from "../components/Footer";
import { motion } from 'framer-motion';

// Assets
import VisionImg from "../assets/vission_icons/vision.png";
import MissionImg from "../assets/Mission_icons/mission.png";

export default function About() {
  return (
    <div className="bg-white text-slate-900">
      <Header />

      <section className="relative w-full pt-44 pb-32 overflow-hidden bg-white">
        {/* Decorative Background Elements */}
        <div className="absolute top-0 left-0 w-full h-full overflow-hidden -z-0">
          <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[60%] bg-blue-50 rounded-full blur-[120px] opacity-60" />
          <div className="absolute bottom-0 right-0 w-[30%] h-[50%] bg-slate-50 rounded-full blur-[100px]" />
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col items-center text-center mb-20">
            <motion.span
              initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className="px-4 py-1.5 rounded-full bg-blue-100 text-blue-700 text-sm font-bold uppercase tracking-widest mb-6"
            >
              Our Journey
            </motion.span>
            <h1 className="text-5xl md:text-8xl font-black leading-[0.9] tracking-tighter mb-8">
              Engineering <span className="text-sky-500/100">Operational</span> <br /> Excellence.
            </h1>
            <p className="max-w-3xl text-xl text-slate-500 font-light leading-relaxed">
              Adeya IMS was born out of a simple necessity: To give Indian manufacturers a system that is as hardworking as they are. No limits, no complexity—just pure operational control.
            </p>
          </div>

          {/* Video ki jagah ye 3 Feature Highlights daal diye hain jo trust build karenge */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mt-12">
            <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 hover:shadow-xl transition-all">
              <div className="text-3xl mb-4">🇮🇳</div>
              <h3 className="text-xl font-bold mb-2">Proudly Indian</h3>
              <p className="text-slate-500 text-sm">Designed specifically for the unique workflows of the Indian MSME sector.</p>
            </div>
            <div className="p-8 bg-sky-500/100 rounded-[2.5rem] text-white shadow-xl shadow-blue-200">
              <div className="text-3xl mb-4">🏗️</div>
              <h3 className="text-xl font-bold mb-2 text-white">Industrial Strength</h3>
              <p className="text-blue-100 text-sm">Built on a high-concurrency architecture that never slows down, no matter the data size.</p>
            </div>
            <div className="p-8 bg-slate-50 rounded-[2.5rem] border border-slate-100 hover:shadow-xl transition-all">
              <div className="text-3xl mb-4">🛠️</div>
              <h3 className="text-xl font-bold mb-2">Zero-Friction</h3>
              <p className="text-slate-500 text-sm">Focused on quick entry and instant reporting so you can focus on production, not paperwork.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ================= VISION & MISSION (Same but polished) ================= */}
      <section className="w-full py-32 border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-24 items-center mb-40">
            <div className="relative">
              <img src={VisionImg} alt="Vision" className="w-full max-w-sm mx-auto drop-shadow-2xl" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-72 h-72 bg-blue-100 blur-[120px] -z-10 rounded-full" />
            </div>
            <div>
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-8 uppercase">The Vision</h2>
              <p className="text-2xl text-slate-500 font-light leading-snug">
                Our mission is to turn every warehouse into a smart operational hub. With Adeya IMS, scale your business without worrying about data limits.
              </p>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-24 items-center">
            <div className="order-2 md:order-1">
              <h2 className="text-4xl md:text-5xl font-black tracking-tighter mb-8 uppercase">The Mission</h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-12">
                To provide a reliable, scalable, and secure infrastructure that tracks every purchase, sales, and movement across unlimited locations with absolute integrity.
              </p>
              <div className="flex gap-12">
                <div>
                  <h4 className="text-4xl font-black text-blue-600">100%</h4>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Accuracy</p>
                </div>
                <div>
                  <h4 className="text-4xl font-black text-blue-600">∞</h4>
                  <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mt-1">Records</p>
                </div>
              </div>
            </div>
            <div className="order-1 md:order-2 relative">
              <img src={MissionImg} alt="Mission" className="w-full max-w-sm mx-auto drop-shadow-2xl" />
            </div>
          </div>
        </div>
      </section>

      {/* ================= OPERATIONAL PILLARS ================= */}
      <section className="w-full py-32 bg-slate-900 text-white rounded-[4rem] mb-12 mx-auto w-[95%]">
        <div className="max-w-6xl mx-auto text-center px-4">
          <h2 className="text-4xl md:text-6xl font-black mb-16 tracking-tighter italic uppercase">Core Pillars</h2>
          <div className="flex flex-wrap justify-center gap-6">
            <PillarBox number="01" title="Visibility" desc="Real-time tracking of every single Warehouse." />
            <PillarBox number="02" title="Scalability" desc="Unlimited records, unlimited potential." />
            <PillarBox number="03" title="Integrity" desc="Data you can trust for your tax & audits." />
            <PillarBox number="04" title="Flexibility" desc="Works the way your business works." />
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function PillarBox({ number, title, desc }) {
  return (
    <div className="w-64 p-8 bg-slate-800 rounded-3xl text-left border border-slate-700 hover:border-blue-500 transition-all group">
      <span className="text-blue-500 font-black text-xl mb-4 block opacity-50 group-hover:opacity-100 transition-opacity">{number}</span>
      <h4 className="text-xl font-bold mb-2">{title}</h4>
      <p className="text-slate-400 text-sm leading-relaxed">{desc}</p>
    </div>
  );
}